import { useEffect, useRef, useState } from 'react';
import { Play, Square } from 'lucide-react';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
} from '../ui/input-group';
import {
  parsePart,
  toParts,
  toSeconds,
  unpaddedPart,
  type TimeParts,
} from './lead-time';
import { cn } from '../../lib/utils';

/**
 * How often the running clock redraws. The thousandths digit therefore steps by
 * about this much rather than counting every one of them — a thumb on a button is
 * nowhere near that precise, and the number that matters is the one the clock
 * hands over when it stops, which is exact to the millisecond.
 */
const TICK_MS = 30;

interface LeadTimeInputProps {
  /** Seconds, with thousandths in the decimals. Null while nothing is entered. */
  value: number | null;
  onChange: (seconds: number | null) => void;
  /**
   * Offer the clock. The judge at the wall starts it as the climber leaves the
   * ground and stops it when they come off; everybody correcting a card
   * afterwards just types.
   */
  stopwatch?: boolean;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * How long a lead climber was on the route: `m : ss . SSS`, three boxes in one
 * bordered field, with the clock at the end of it.
 *
 * **The clock is a way of filling the boxes, not a second source of truth.** While
 * it runs the field shows what it has counted and the boxes are read-only; when it
 * stops, that time lands in them and is a draft like any other — the judge can
 * correct it before the card is saved, which is the whole reason the boxes are
 * there. Nothing is lifted to the caller until the clock stops, so a table with
 * one row open is not re-rendered thirty times a second for the length of a climb.
 *
 * **A box being typed in shows what was typed, not the padded value.** The field
 * reads back as `2:05.500`, and a box showing `05` that quietly refuses the digit
 * you add to it is the single most annoying thing a time input can do — so while a
 * box has focus it holds the digits themselves (`unpaddedPart`), its contents are
 * selected on the way in so typing replaces, and the padding comes back when the
 * focus leaves.
 *
 * Still fully controlled, so **a cleared box reads as zero against the other two**
 * once the focus has left it — `:30` with the minutes rubbed out is still thirty
 * seconds, and a time cannot be emptied back to nothing once it has one. A card
 * comes off the board by clearing its *holds* (see `LeadCardEditor`).
 */
export function LeadTimeInput({
  value,
  onChange,
  stopwatch = false,
  id,
  disabled,
  invalid,
  className,
  'data-testid': testId,
}: LeadTimeInputProps) {
  /** Whether the clock is counting, and the milliseconds it has counted. */
  const [ticking, setTicking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(0);

  /**
   * The box being typed in and the digits in it. While one is being typed in it
   * shows these rather than its padded value, which is what keeps the padding from
   * swallowing the digit (see the note above).
   */
  const [typing, setTyping] = useState<keyof TimeParts | null>(null);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!ticking) return;

    const timer = window.setInterval(() => {
      setElapsed(Date.now() - startedAt.current);
    }, TICK_MS);

    return () => window.clearInterval(timer);
  }, [ticking]);

  const parts = ticking ? toParts(elapsed / 1000) : toParts(value);

  const emit = (next: TimeParts) => onChange(toSeconds(next));

  const handlePart = (key: keyof TimeParts, raw: string) => {
    const kept = parsePart(key, raw);
    setTyped(kept);
    emit({ ...parts, [key]: kept });
  };

  const toggleClock = () => {
    if (ticking) {
      // The clock stops and hands over what it counted, to the millisecond; from
      // here it is a draft like any typed time.
      onChange((Date.now() - startedAt.current) / 1000);
      setTicking(false);
      return;
    }

    startedAt.current = Date.now();
    setElapsed(0);
    setTicking(true);
  };

  const box = (key: keyof TimeParts, label: string, width: string) => (
    <InputGroupInput
      id={key === 'minutes' ? id : undefined}
      type="text"
      inputMode="numeric"
      aria-label={label}
      value={typing === key && !ticking ? typed : parts[key]}
      readOnly={ticking}
      disabled={disabled}
      placeholder={key === 'minutes' ? '0' : key === 'seconds' ? '00' : '000'}
      onFocus={(event) => {
        const field = event.currentTarget;
        setTyping(key);
        setTyped(unpaddedPart(parts[key]));
        // Selected on the way in, so a judge who means to replace the value just
        // types it. After the frame, not during it: the box is about to be
        // re-rendered with its padding off, and a selection made now would be
        // dropped by that change.
        requestAnimationFrame(() => field.select());
      }}
      onBlur={() => setTyping(null)}
      onChange={(event) => handlePart(key, event.target.value)}
      // Each box is its own width — a time is read as a shape, and letting one
      // stretch would move the separators about as somebody types — and wide
      // enough for its digits plus the one more that typing briefly adds.
      className={cn('flex-none text-center tabular-nums', width)}
      data-testid={testId ? `${testId}-${key}` : `lead-time-${key}`}
    />
  );

  return (
    <InputGroup
      className={cn('w-full justify-center', invalid && 'border-destructive', className)}
      aria-invalid={invalid || undefined}
      data-testid={testId}
    >
      {box('minutes', 'min', 'w-9')}
      <InputGroupText aria-hidden>:</InputGroupText>
      {box('seconds', 'sec', 'w-9')}
      <InputGroupText aria-hidden>.</InputGroupText>
      {box('thousandths', '1/1000', 'w-12')}

      {stopwatch && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            aria-pressed={ticking}
            aria-label={ticking ? 'stop' : 'start'}
            disabled={disabled}
            onClick={toggleClock}
            className={cn(ticking && 'aria-[pressed=true]:bg-destructive')}
            data-testid={testId ? `${testId}-clock` : 'lead-time-clock'}
          >
            {ticking ? <Square className="size-3.5" aria-hidden /> : <Play className="size-3.5" aria-hidden />}
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
}
